/**
 * Normalize whitespace inside Intl.NumberFormat output.
 *
 * Hydration-mismatch root cause: Node's bundled ICU and the browser's ICU
 * disagree on which Unicode space goes between the number and the currency
 * symbol. Node typically emits U+00A0 (regular non-breaking space) while
 * modern Chrome/Firefox emit U+202F (narrow non-breaking space). The two
 * bytes differ, so React's text-content compare during hydration fails on
 * EVERY price node (PDP shows ~6 prices → ~6 #418/#425 errors per load,
 * times the variant pickers / recommendations rail = ~40 errors).
 *
 * Collapsing both NBSP variants to a regular ASCII space gives identical
 * SSR + CSR output. Visually indistinguishable to the user.
 */
function normalizeIntlSpace(s: string): string {
  return s.replace(/[  ]/g, " ");
}

export function formatMoney(
  amount: number | string,
  currency: string = "VND"
): string {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return normalizeIntlSpace(
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(num),
  );
}

export function parseMoney(value: string): number {
  return parseFloat(value.replace(/[^0-9.-]/g, ""));
}

/**
 * M-17: Centralized VND formatter — replaces duplicate inline formatVND()
 * functions in rewards, account, and admin routes.
 */
export function formatVND(amount: number | string): string {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return normalizeIntlSpace(
    new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
      maximumFractionDigits: 0,
    }).format(num),
  );
}

/**
 * Format price for storefront display (locale-aware).
 *
 * T1.4: previously hardcoded to "vi-VN" regardless of currency, causing USD
 * prices on the international market to render as "22,00 US$" instead of
 * "$22.00". Now accepts an optional `locale` parameter and picks the
 * appropriate Intl locale tag.
 *
 * Audit 2026-04-24: REMOVED the `currency = "VND"` default. The previous
 * default silently labelled USD/SGD/EUR product prices with the ₫ symbol
 * across PDP, cart, mini-cart, recommendations and product cards because
 * 20+ callers omitted the currency arg. Currency is now REQUIRED so any
 * future omission surfaces at compile time. Pass the active market
 * currency from `useOutletContext().currency`, or `order.currency` for
 * historical orders, or `shop.currency` as a deliberate base-currency
 * fallback (NOT a default).
 */
export function formatStorefrontPrice(
  amount: number | string,
  currency: string,
  locale?: "vi" | "en" | "th"
): string {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  // W14 (Thailand): THB always renders with the ฿ symbol + no decimals
  // (Thai retail convention) — en-US would emit a literal "THB 1,290.00".
  const intlLocale =
    currency === "THB"
      ? "th-TH"
      : locale
        ? (locale === "en" ? "en-US" : "vi-VN")
        : (currency === "VND" ? "vi-VN" : "en-US");
  // Hayden 2026-08-10: whole amounts drop the cents entirely — "$30", not
  // "$30.00" (international-site report). Fractional amounts keep BOTH
  // digits so $29.50 never collapses to "$29.5".
  const zeroDecimal = currency === "VND" || currency === "THB";
  const isWhole = Number.isFinite(num) && Math.abs(num % 1) < 1e-9;
  const fractionDigits = zeroDecimal || isWhole ? 0 : 2;
  return normalizeIntlSpace(
    new Intl.NumberFormat(intlLocale, {
      style: "currency",
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(num),
  );
}
